import { CashRegisterConnected } from "@/components/CashRegister/CashRegisterConnected"
import { CashRegisterDisconnected } from "@/components/CashRegister/CashRegisterDisconnected"
import { HeaderCashRegister } from "@/components/CashRegister/HeaderCashRegister"
import {
  CashRegisterHistoricModal,
  CashRegisterWithBookingModal,
  CashRegisterWithoutAppointmentModal,
} from "@/components/CashRegister/modals"
import { AddCashRegisterModal } from "@/components/CashRegister/modals/AddCashRegisterModal"
import { AddTerminalToCashRegisterModal } from "@/components/CashRegister/modals/AddTerminalToCashRegisterModal"
import { ManageReadersModal } from "@/components/CashRegister/modals/ManageReadersModal"
import { RegisterReaderModal } from "@/components/CashRegister/modals/registerReaders/RegisterReaderModal"
import { StripeTerminalSetup } from "@/components/CashRegister/paymentCB/components/StripeTerminalSetup"
import {
  useCashRegistersQuery,
  useUnlockCashRegisterMutation,
} from "@/query/cashRegisterQuery"
import { useCurrentUserQuery } from "@/query/currentUserQuery"
import { useStripeReadersQuery } from "@/query/stripeTerminalQuery"
import {
  setOpenModalAddCashRegister,
  setOpenModalAddTerminalToCashRegister,
  setOpenModalManageReaders,
} from "@/store/modalsReducers/modalSlice"
import { useAppDispatch, useAppSelector } from "@/store/store"
import { getCurrentCashRegister } from "@/utils/functions"
import { Button, Card } from "@okeys/shared-react-components"
import { Loader2, LogOut, Plus, Settings } from "lucide-react"
import React, { useState } from "react"

const CashRegister: React.FC = () => {
  const dispatch = useAppDispatch()
  const isAddCashRegisterModalOpen = useAppSelector(
    (state) => state.modal.openModalAddCashRegister.isOpen
  )
  const isManageReadersModalOpen = useAppSelector(
    (state) => state.modal.openModalManageReaders.isOpen
  )
  const isAddTerminalToCashRegisterModalOpen = useAppSelector(
    (state) => state.modal.openModalAddTerminalToCashRegister.isOpen
  )
  const addTerminalToCashRegisterCaisseId = useAppSelector(
    (state) => state.modal.openModalAddTerminalToCashRegister.caisseId
  )
  const [isRegisterReaderModalOpen, setIsRegisterReaderModalOpen] =
    useState(false)

  // Récupérer les données nécessaires
  const {
    currentUser,
    isLoading: isLoadingCurrentUser,
    isPending: isPendingCurrentUser,
    isError: isErrorCurrentUser,
  } = useCurrentUserQuery()
  const {
    cashRegisters,
    pendingCashRegisters,
    error: errorCashRegisters,
  } = useCashRegistersQuery()
  const {
    readers,
    isLoading: isLoadingReaders,
    isError: isErrorReaders,
  } = useStripeReadersQuery()
  const unlockCashRegisterMutation = useUnlockCashRegisterMutation()

  const currentCashRegister = getCurrentCashRegister(currentUser, cashRegisters)

  if (
    isLoadingCurrentUser ||
    pendingCashRegisters ||
    isPendingCurrentUser ||
    isLoadingReaders
  ) {
    // TODO: Add loading component
    return <div>Loading...</div>
  }

  if (isErrorCurrentUser || errorCashRegisters || isErrorReaders) {
    return <div>Error</div>
  }

  const handleOpenAddCashRegister = () => {
    dispatch(setOpenModalAddCashRegister({ isOpen: true }))
  }

  const handleCloseAddCashRegister = () => {
    dispatch(setOpenModalAddCashRegister({ isOpen: false }))
  }

  const handleOpenManageReaders = () => {
    dispatch(setOpenModalManageReaders({ isOpen: true }))
  }

  const handleCloseManageReaders = () => {
    dispatch(setOpenModalManageReaders({ isOpen: false }))
  }

  const handleOpenAddTerminalToCashRegister = () => {
    dispatch(
      setOpenModalAddTerminalToCashRegister({
        isOpen: true,
        caisseId: currentCashRegister?.caisseId,
      })
    )
  }

  const handleDisconnectFromCashRegister = async () => {
    if (!currentCashRegister) {
      console.error("Aucune caisse courante trouvée")
      return
    }

    try {
      await unlockCashRegisterMutation.mutateAsync({
        caisseId: currentCashRegister.caisseId,
      })
    } catch (error) {
      console.error("Erreur lors de la déconnexion de la caisse:", error)
    }
  }

  const handleCloseAddTerminalToCashRegister = () => {
    dispatch(setOpenModalAddTerminalToCashRegister({ isOpen: false }))
  }

  // Si une caisse est sélectionnée, afficher l'interface principale
  return (
    <>
      <div className="h-full flex flex-col space-y-4 ">
        {/* En-tête avec info de la caisse sélectionnée */}
        <div className="flex justify-between items-center w-full gap-2">
          <Button
            onClick={handleOpenManageReaders}
            variant="outline"
            className="flex items-center gap-2"
            size="sm"
          >
            <Settings className="h-4 w-4" />
            Gérer les terminaux
          </Button>

          {currentCashRegister ? (
            <Button
              variant="destructive"
              onClick={handleDisconnectFromCashRegister}
              disabled={unlockCashRegisterMutation.isPending}
              size="sm"
            >
              {unlockCashRegisterMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LogOut className="h-4 w-4" />
              )}
              {unlockCashRegisterMutation.isPending
                ? "Déconnexion..."
                : "Se déconnecter de la caisse"}
            </Button>
          ) : (
            <Button
              onClick={handleOpenAddCashRegister}
              className="flex items-center gap-2"
              size="sm"
            >
              <Plus className="h-4 w-4" />
              Ajouter une caisse
            </Button>
          )}
        </div>

        {/* Boutons principaux en colonnes */}
        <Card className="h-full relative border-border flex flex-col gap-2">
          <HeaderCashRegister
            type={
              currentCashRegister ? "Connected" : "disconnectedWithCashRegister"
            }
            readers={readers || []}
            currentUser={currentUser}
            cashRegisters={cashRegisters}
            onOpenAddTerminalModal={handleOpenAddTerminalToCashRegister}
          />
          {currentCashRegister ? (
            <CashRegisterConnected />
          ) : (
            <CashRegisterDisconnected
              onOpenAddModal={handleOpenAddCashRegister}
            />
          )}
        </Card>
      </div>

      {/* Modales */}
      <ManageReadersModal
        isOpen={isManageReadersModalOpen}
        onClose={handleCloseManageReaders}
      />
      <AddTerminalToCashRegisterModal
        isOpen={isAddTerminalToCashRegisterModalOpen}
        onClose={handleCloseAddTerminalToCashRegister}
        onOpenManageReaders={handleOpenManageReaders}
        caisseId={addTerminalToCashRegisterCaisseId}
      />
      <CashRegisterWithoutAppointmentModal />
      <CashRegisterWithBookingModal />
      <CashRegisterHistoricModal />
      <RegisterReaderModal
        isOpen={isRegisterReaderModalOpen}
        onClose={() => setIsRegisterReaderModalOpen(false)}
        shopId={currentCashRegister?.shopId || ""}
      />
      <AddCashRegisterModal
        isOpen={isAddCashRegisterModalOpen}
        onClose={handleCloseAddCashRegister}
        onOpenManageReaders={handleOpenManageReaders}
      />

      {/* Composant de configuration Stripe Terminal (uniquement en dev) */}
      {import.meta.env.DEV && <StripeTerminalSetup />}
    </>
  )
}

export default CashRegister
